<script setup>
import {useStore} from "vuex";
import {computed, onMounted, ref, watch} from "vue";
import {COMMENT_PRIORITIES, COMMENT_STATUSES, priorityDisplay, statusDisplay, toDueDate} from "@/scripts/Comment";
import IssueLabels from "@/components/viewer/IssueLabels.vue";

const store = useStore();

// eslint-disable-next-line no-undef
const props = defineProps({
  loadingModel: Boolean
});

// Variables
const newComment   = ref(null);
const userReplies  = ref({});
const openComment  = ref(null);
const dueDateMenus = ref({});

// Computed properties
const comments = computed(() => {
  return store.getters.getComments;
});

// Methods
const addComment = async () => {
  // Captured from the live viewer so the issue is pinned to the model with a screenshot
  // and camera; the comments card has no viewer and creates untethered issues instead.
  const threeD = await store.dispatch('captureViewerState');
  store.dispatch('addNewComment', {
    text: newComment.value,
    threeD
  });
  newComment.value = null;
};

const sendReply = (commentId) => {
  store.dispatch('addReplyToComment', {
    threadId: commentId,
    text    : userReplies.value[commentId]
  });
  delete userReplies.value[commentId];
}

const changeStatus = (commentId, status) => {
  store.dispatch('updateCommentStatus', {
    commentId: commentId,
    status   : status
  });
};

const changePriority = (commentId, priority) => {
  store.dispatch('updateCommentPriority', {
    commentId: commentId,
    priority : priority
  });
};

const changeDueDate = (commentId, date) => {
  store.dispatch('updateCommentDueDate', {
    commentId: commentId,
    dueDate  : date ? toDueDate(date) : null
  });
  dueDateMenus.value[commentId] = false;
};

const updateOpenGroup = (isOpen, commentId) => {
  if (isOpen) {
    openComment.value = commentId;
  } else {
    openComment.value = null;
  }
};

const handleCommentSelectionDisplay = (value, fnName) => {
  if (value) {
    const id = getSelectedObjectIds(value);
    if (id) {
      store.commit(fnName, {
        ids: id
      });
    }
  }
};

const getSelectedObjectIds = (id) => {
  const comment = comments.value.filter((comment) => comment.id === id);
  const selectedApplicationObject = comment[0]?.comment?.viewerState?.ui?.filters?.selectedObjectApplicationIds;
  if (!selectedApplicationObject || Object.keys(selectedApplicationObject).length === 0) {
    return null;
  }
  return Object.keys(selectedApplicationObject)[0];
};

watch(openComment, (newValue, previousValue) => {
  handleCommentSelectionDisplay(previousValue, "unselectObjects");
  handleCommentSelectionDisplay(newValue, "selectObjects");
});

// Lifecycle hooks
onMounted(
    () => {
      store.dispatch('retrieveComments');
    }
);
</script>

<template>
  <div>
    <v-textarea v-model="newComment"
                clearable
                variant="outlined"
                :label="$t('placeholders.newComment')"
                class="comment-input"
                rows="3"
                auto-grow
                :disabled="props.loadingModel"
    ></v-textarea>
    <div class="comment-actions">
      <v-btn color="secondary"
             density="comfortable"
             position="relative"
             @click="addComment()"
             :disabled="props.loadingModel"
      >
        {{ $t('add') }}
      </v-btn>
    </div>
  </div>
  <v-list class="comments-list"
          max-height="322"
          open-strategy="single"
          density="compact"
          :disabled="props.loadingModel"
  >
    <v-list-group v-for="comment in comments"
                  :key="comment.id"
    >
      <template v-slot:activator="{ isOpen, props: activatorProps }">
        <v-list-item v-bind="activatorProps"
                     @click="updateOpenGroup(!isOpen, comment.id)"
        >
          <div class="comment-status-row">
            <v-chip class="comment-status-chip"
                    size="small"
                    variant="tonal"
                    :color="comment.getStatusColor()"
                    :prepend-icon="comment.getStatusIcon()"
                    :append-icon="comment.canEditStatus ? 'mdi-menu-down' : undefined"
                    :disabled="loadingModel"
                    @click.stop
            >
              {{ comment.getStatusLabel() }}
              <v-menu v-if="comment.canEditStatus && !loadingModel"
                      activator="parent"
              >
                <v-list class="comment-status-menu" density="compact">
                  <v-list-subheader>{{ $t('comments.status.title') }}</v-list-subheader>
                  <v-list-item v-for="status in COMMENT_STATUSES"
                               :key="status"
                               :active="status === comment.status"
                               @click="changeStatus(comment.id, status)"
                  >
                    <template v-slot:prepend>
                      <v-icon :color="statusDisplay(status).color"
                              :icon="statusDisplay(status).icon"
                              size="small"
                      ></v-icon>
                    </template>
                    <v-list-item-title>{{ $t(`comments.status.${status}`) }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-chip>
            <v-chip class="comment-priority-chip"
                    size="small"
                    variant="tonal"
                    :color="comment.getPriorityColor()"
                    :prepend-icon="comment.getPriorityIcon()"
                    :append-icon="comment.canEditPriority ? 'mdi-menu-down' : undefined"
                    :disabled="loadingModel"
                    @click.stop
            >
              {{ comment.getPriorityLabel() }}
              <v-menu v-if="comment.canEditPriority && !loadingModel"
                      activator="parent"
              >
                <v-list class="comment-status-menu" density="compact">
                  <v-list-subheader>{{ $t('comments.priority.title') }}</v-list-subheader>
                  <v-list-item v-for="priority in COMMENT_PRIORITIES"
                               :key="priority"
                               :active="priority === comment.priority"
                               @click="changePriority(comment.id, priority)"
                  >
                    <template v-slot:prepend>
                      <v-icon :color="priorityDisplay(priority).color"
                              :icon="priorityDisplay(priority).icon"
                              size="small"
                      ></v-icon>
                    </template>
                    <v-list-item-title>{{ $t(`comments.priority.${priority}`) }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-chip>
            <v-chip v-if="comment.hasDueDate() || comment.canEditDueDate"
                    class="comment-duedate-chip"
                    size="small"
                    variant="tonal"
                    :color="comment.getDueDateColor()"
                    :prepend-icon="comment.getDueDateIcon()"
                    :append-icon="comment.canEditDueDate ? 'mdi-menu-down' : undefined"
                    :disabled="loadingModel"
                    @click.stop
            >
              {{ comment.hasDueDate() ? comment.getFormattedDueDate() : $t('comments.dueDate.set') }}
              <v-menu v-if="comment.canEditDueDate && !loadingModel"
                      v-model="dueDateMenus[comment.id]"
                      activator="parent"
                      :close-on-content-click="false"
              >
                <v-card class="comment-duedate-menu">
                  <v-date-picker hide-header
                                 :model-value="comment.hasDueDate() ? new Date(comment.dueDate) : undefined"
                                 @update:model-value="changeDueDate(comment.id, $event)"
                  ></v-date-picker>
                  <v-card-actions v-if="comment.hasDueDate()">
                    <v-btn color="secondary"
                           density="comfortable"
                           prepend-icon="mdi-calendar-remove"
                           @click="changeDueDate(comment.id, null)"
                    >
                      {{ $t('comments.dueDate.clear') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-chip>
            <issue-labels :comment="comment"></issue-labels>
          </div>
          <div class="comment-body">
            <v-avatar color="secondary">
              <span class="text-h5">{{ comment.getAuthorAcronym() }}</span>
            </v-avatar>
            <div class="comment-content">
              <v-list-item-title>
                {{ comment.getFormattedCommentCreatedDate() }}
              </v-list-item-title>
              <div v-if="comment.title" class="text-subtitle-2 font-weight-bold comment-title">
                {{ comment.title }}
              </div>
              <div class="text-subtitle-2 text-medium-emphasis" v-html="comment.getStringifiedText()">
              </div>
            </div>
          </div>
        </v-list-item>
      </template>
      <v-list-item v-for="reply in comment.replies" :key="reply.id" lines="three">
        <template v-slot:prepend>
          <v-avatar color="secondary">
            <span class="text-h5">{{ reply.getAuthorAcronym() }}</span>
          </v-avatar>
        </template>
        <v-list-item-title>{{ reply.getFormattedCommentCreatedDate() }}</v-list-item-title>
        <div class="text-subtitle-2 text-medium-emphasis" v-html="reply.getStringifiedText()"></div>
      </v-list-item>
      <div v-if="!comment.isResolved()">
        <v-textarea v-model="userReplies[comment.id]"
                    clearable
                    variant="outlined"
                    :label="$t('placeholders.replyThread')"
                    class="comment-reply-input"
                    rows="3"
                    auto-grow
        ></v-textarea>
        <div class="comment-reply-actions">
          <v-btn color="secondary"
                 density="comfortable"
                 position="relative"
                 @click="sendReply(comment.id)"
          >
            {{ $t('reply') }}
          </v-btn>
        </div>
      </div>
    </v-list-group>
  </v-list>
</template>

<style scoped>
@import '@/assets/styles/components/viewer/ModelComments.css';
</style>